import React, { useState } from 'react';
import {
  FaLock,
  FaPlug,
  FaCreditCard,
  FaEnvelope,
  FaPhoneAlt,
  FaCommentDots,
  FaUserShield,
  FaUserCog,
  FaUser,
  FaPaperPlane,
  FaInfoCircle,
  FaCopyright,
  FaBuilding,
  FaTag,
  FaCodeBranch,
} from 'react-icons/fa';
import { MdKeyboardArrowRight } from 'react-icons/md';

const Help = () => {
  const [activeSection, setActiveSection] = useState('quickStart');

  const sections = {
    quickStart: 'Quick Start Guide',
    about: 'About', // New About section
    faqs: 'FAQs',
    troubleshooting: 'Troubleshooting',
    contact: 'Contact Support',
    userPermissions: 'User Permissions and Roles',
    feedback: 'Feedback Section',
  };

  const content = {
    quickStart: (
      <div className="p-2 mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {/* Step 1: Admin Registration */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">1. Register an Admin Account</h4>
            <p className="text-gray-600 text-sm mb-2">
              Go to the registration page and create your admin account. Verify your email to
              activate the account.
            </p>
          </div>

          {/* Step 2: Admin Login */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">2. Log In as Admin</h4>
            <p className="text-gray-600 text-sm mb-2">
              Login using your verified email and password to access the property setup.
            </p>
          </div>

          {/* Step 3: Register Property */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">3. Set Up Your Property</h4>
            <p className="text-gray-600 text-sm mb-2">
              Register your property's details including name, logo, images, and essential settings.
            </p>
          </div>

          {/* Step 4: Access Dashboard */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">
              4. Navigate to Admin Dashboard
            </h4>
            <p className="text-gray-600 text-sm mb-2">
              After property setup, access the dashboard to manage users, rooms, bookings, and
              settings.
            </p>
          </div>

          {/* Step 5: Add and Manage Users */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">5. Add Staff Users</h4>
            <p className="text-gray-600 text-sm mb-2">
              Add users with temporary passwords sent via email along with a verification code.
              Assign their roles and permissions.
            </p>
          </div>

          {/* Step 6: User Login */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">6. Staff User Login</h4>
            <p className="text-gray-600 text-sm mb-2">
              Staff members login using their temporary credentials, set their password, and start
              accessing assigned modules.
            </p>
          </div>

          {/* Step 7: Property Management System (PMS) */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">
              7. Manage Front Desk Operations
            </h4>
            <p className="text-gray-600 text-sm mb-2">
              Handle room assignments, check-ins/outs, guest arrivals, cancellations, and
              reservations through the PMS.
            </p>
          </div>

          {/* Step 8: Hospitality and Billing */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">
              8. Manage Room Services & Payments
            </h4>
            <p className="text-gray-600 text-sm mb-2">
              Process amenities requests, food orders, room service, and handle billing and online
              payments from guests.
            </p>
          </div>

          {/* Step 9: Revenue Management */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">
              9. Set Pricing & Revenue Templates
            </h4>
            <p className="text-gray-600 text-sm mb-2">
              Admins can set room rates based on different factors and create seasonal or special
              pricing templates.
            </p>
          </div>

          {/* Step 10: Master Data Management */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">10. Configure Master Data</h4>
            <p className="text-gray-600 text-sm mb-2">
              Manage property policies, rooms, amenities, services, menus, and corporate clients
              under Master Data Management.
            </p>
          </div>

          {/* Step 11: Customize User Settings */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">
              11. User Settings and Security
            </h4>
            <p className="text-gray-600 text-sm mb-2">
              Enable or disable 2FA, reset passwords, and manage user profile settings for better
              security and control.
            </p>
          </div>

          {/* Step 12: Dashboard Overview */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-base font-semibold text-gray-700">
              12. Monitor Everything from Dashboard
            </h4>
            <p className="text-gray-600 text-sm mb-2">
              View real-time data, key performance stats, bookings overview, revenue reports, and
              important notifications.
            </p>
          </div>
        </div>
      </div>
    ),

    // New About section with copyright information
    about: (
      <div className="p-6 max-w-5xl mx-auto mt-2">
        <div className="bg-gradient-to-r from-gray-50 to-white p-8 rounded-xl shadow-lg border border-gray-200">
          <div className="flex items-center mb-8">
            <FaInfoCircle className="text-gray-600 w-10 h-10 mr-4" />
            <h3 className="text-3xl font-bold text-gray-800">About This Software</h3>
          </div>

          {/* Product Information Card */}
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-gray-500 mb-6">
            <div className="space-y-4">
              {/* Product Name */}
              <div className="flex items-start space-x-4">
                <FaTag className="text-gray-600 w-5 h-5 mt-1" />
                <div>
                  <span className="text-sm font-medium text-gray-500">Product Name</span>
                  <p className="text-xl font-semibold text-gray-800">EnterpriseQuest (EQ)</p>
                </div>
              </div>

              {/* Version */}
              <div className="flex items-start space-x-4">
                <FaCodeBranch className="text-gray-600 w-5 h-5 mt-1" />
                <div>
                  <span className="text-sm font-medium text-gray-500">Version</span>
                  <p className="text-lg text-gray-700">1.0.0</p>
                </div>
              </div>

              {/* Company Name */}
              <div className="flex items-start space-x-4">
                <FaBuilding className="text-gray-600 w-5 h-5 mt-1" />
                <div>
                  <span className="text-sm font-medium text-gray-500">Company</span>
                  <p className="text-lg text-gray-700">Rhombus Global Services</p>
                </div>
              </div>

              {/* Copyright Line */}
              <div className="flex items-start space-x-4">
                <FaCopyright className="text-gray-600 w-5 h-5 mt-1" />
                <div>
                  <span className="text-sm font-medium text-gray-500">Copyright</span>
                  <p className="text-lg text-gray-700">
                    © 2026 Rhombus Global Services. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* License Information */}
          <div className="bg-gray-50 p-6 rounded-lg shadow-md">
            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <FaInfoCircle className="text-gray-600 w-5 h-5 mr-2" />
              License Information
            </h4>
            <p className="text-gray-600 leading-relaxed">
              This software is licensed under the Rhombus Global Services Enterprise License
              Agreement. Unauthorized copying, distribution, or modification of this software is
              strictly prohibited and may result in legal action. For licensing inquiries, please
              contact
              <a
                href="mailto:admin@rhombusquest.com"
                className="text-gray-600 hover:underline ml-1"
              >
                admin@rhombusquest.com
              </a>
            </p>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-sm text-gray-500 text-center">
            <p>This software is protected by copyright laws and international treaties.</p>
            <p className="mt-1">
              For support, please visit our help center or contact customer service.
            </p>
          </div>
        </div>
      </div>
    ),

    faqs: (
      <div className=" p-6  max-w-5xl mx-auto mt-2">
        <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">
          Frequently Asked Questions
        </h3>

        <div className="space-y-6">
          {/* Question 1 */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-lg font-semibold text-gray-800">How do I add a new booking?</h4>
            <p className="text-gray-600 text-base mt-2">
              Navigate to the <span className="font-semibold text-gray-900">"Bookings"</span>{' '}
              section and click <span className="font-semibold text-gray-900">"Add Booking"</span>.
              Fill out the necessary details and confirm your booking.
            </p>
          </div>

          {/* Question 2 */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-lg font-semibold text-gray-800">
              How do I update room availability?
            </h4>
            <p className="text-gray-600 text-base mt-2">
              Go to the <span className="font-semibold text-gray-900">"Rooms"</span> section and
              adjust the availability settings. You can specify which rooms are available or
              unavailable for booking.
            </p>
          </div>

          {/* Question 3 */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <h4 className="text-lg font-semibold text-gray-800">What if a payment fails?</h4>
            <p className="text-gray-600 text-base mt-2">
              Check the <span className="font-semibold text-gray-900">"Payments"</span> tab for
              details about the failed transaction and retry the payment or contact support for
              assistance.
            </p>
          </div>
        </div>
      </div>
    ),
    troubleshooting: (
      <div className=" p-6  max-w-5xl mx-auto mt-8">
        <div className="space-y-4">
          {/* Troubleshooting Item 1 */}
          <div className="flex items-start space-x-4 bg-gray-100 p-4 rounded-lg shadow-sm hover:bg-gray-200 transition">
            <div className="flex-shrink-0">
              <FaLock className="w-6 h-6 text-yellow-500" />
            </div>
            <div className="flex-grow">
              <h4 className="text-lg font-semibold text-gray-800">Unable to log in?</h4>
              <p className="text-gray-600 text-sm mt-2">
                Reset your password or contact support. Ensure that your account is active, and
                check your internet connection.
              </p>
            </div>
          </div>

          {/* Troubleshooting Item 2 */}
          <div className="flex items-start space-x-4 bg-gray-100 p-4 rounded-lg shadow-sm hover:bg-gray-200 transition">
            <div className="flex-shrink-0">
              <FaPlug className="w-6 h-6 text-green-500" />
            </div>
            <div className="flex-grow">
              <h4 className="text-lg font-semibold text-gray-800">
                Sync issues with third-party services?
              </h4>
              <p className="text-gray-600 text-sm mt-2">
                Reconnect your integrations in the settings. Ensure that all permissions are
                correctly granted to the service.
              </p>
            </div>
          </div>

          {/* Troubleshooting Item 3 */}
          <div className="flex items-start space-x-4 bg-gray-100 p-4 rounded-lg shadow-sm hover:bg-gray-200 transition">
            <div className="flex-shrink-0">
              <FaCreditCard className="w-6 h-6 text-red-500" />
            </div>
            <div className="flex-grow">
              <h4 className="text-lg font-semibold text-gray-800">Payment gateway errors?</h4>
              <p className="text-gray-600 text-sm mt-2">
                Verify your API keys, check the provider's status page for any ongoing issues, or
                try contacting support for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    ),
    contact: (
      <div className="p-6 max-w-3xl mx-auto mt-8">
        <div className="space-y-4">
          {/* Email Support */}
          <div className="flex items-center gap-5 p-5 bg-gradient-to-r from-blue-50 to-white rounded-xl shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
            <div className="p-3 bg-blue-100 rounded-full">
              <FaEnvelope className="text-blue-600 w-5 h-5" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-800">Email Support</h4>
              <p className="text-sm text-gray-600">admin@rhombusquest.com</p>
              <p className="text-xs text-gray-500 mt-1">📬 24/7 support available</p>
            </div>
          </div>

          {/* Phone Support */}
          <div className="flex items-center gap-5 p-5 bg-gradient-to-r from-green-50 to-white rounded-xl shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-green-100">
            <div className="p-3 bg-green-100 rounded-full">
              <FaPhoneAlt className="text-green-600 w-5 h-5" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-800">
                Phone Support{' '}
                <span className="text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full ml-2">
                  Coming Soon
                </span>
              </h4>
              <p className="text-sm text-gray-500">
                We're working on it! In the meantime, please reach out via email.
              </p>
              {/* <p className="text-xs text-gray-500 mt-1">📞 24/7 support available</p> */}
            </div>
          </div>

          {/* Live Chat (Coming Soon) */}
          <div className="flex items-center gap-5 p-5 bg-gradient-to-r from-yellow-50 to-white rounded-xl shadow-md border border-yellow-100 opacity-80">
            <div className="p-3 bg-yellow-100 rounded-full">
              <FaCommentDots className="text-yellow-600 w-5 h-5" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-800">
                Live Chat{' '}
                <span className="text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full ml-2">
                  Coming Soon
                </span>
              </h4>
              <p className="text-sm text-gray-500">
                We're working on it! In the meantime, please reach out via email.
              </p>
            </div>
          </div>
        </div>

        {/* Optional: Trust/Footer Line */}
        <p className="text-center text-xs text-gray-400 mt-6 border-t pt-4">
          Rhombus Global Services — We're here to help, anytime.
        </p>
      </div>
    ),

    userPermissions: (
      <div className="max-w-7xl mx-auto p-2">
        {/* Roles Container */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Admin Card */}
          <div className="bg-red-100 p-6 rounded-lg shadow-lg hover:shadow-2xl transition transform hover:scale-105">
            <div className="flex items-center space-x-4">
              <FaUserShield className="text-red-600 w-10 h-10" />
              <h4 className="text-xl font-semibold text-gray-800">Admin</h4>
            </div>
            <p className="text-gray-600 mt-4">
              Full access to all features and settings. This role can manage users, view all
              reports, and adjust system settings.
            </p>
          </div>

          {/* Manager Card */}
          <div className="bg-blue-100 p-6 rounded-lg shadow-lg hover:shadow-2xl transition transform hover:scale-105">
            <div className="flex items-center space-x-4">
              <FaUserCog className="text-blue-600 w-10 h-10" />
              <h4 className="text-xl font-semibold text-gray-800">Manager</h4>
            </div>
            <p className="text-gray-600 mt-4">
              Manage bookings, assign rooms, and view detailed reports. Limited settings
              adjustments.
            </p>
          </div>

          {/* Staff Card */}
          <div className="bg-green-100 p-6 rounded-lg shadow-lg hover:shadow-2xl transition transform hover:scale-105">
            <div className="flex items-center space-x-4">
              <FaUser className="text-green-600 w-10 h-10" />
              <h4 className="text-xl font-semibold text-gray-800">Staff</h4>
            </div>
            <p className="text-gray-600 mt-4">
              Limited access to specific features such as managing bookings and viewing reports.
              Ideal for hotel staff.
            </p>
          </div>
        </div>
      </div>
    ),
    feedback: (
      <div className="max-w-7xl mx-auto p-6 bg-white shadow-lg rounded-lg mb-8">
        <h3 className="text-2xl font-semibold text-gray-800 mb-4">Feedback Section</h3>
        <p className="text-gray-600 mb-4">
          We value your feedback! Submit your suggestions or report issues using the form below.
          Your input helps us improve.
        </p>

        {/* Feedback Form */}
        <div className="space-y-4">
          <textarea
            placeholder="Write your feedback here..."
            className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:outline-none "
            rows="8"
          ></textarea>
          <div className="flex justify-between items-center">
            <button className="bg-cyan-500 text-white hover:text-black px-6 py-2 rounded-full flex items-center space-x-2 ">
              <FaPaperPlane className="w-5 h-5" />
              <span>Submit Feedback</span>
            </button>
          </div>
        </div>
      </div>
    ),
  };

  return (
    <div className="flex h-[900px] bg-gray-50">
      {/* Sidebar */}
      <div className="w-1/5 bg-gray-100 border-r text-black hover:text-gray-800 p-4">
        <h1 className="text-xl font-bold mb-4">Help Center</h1>
        <ul>
          {Object.entries(sections).map(([key, title]) => (
            <li
              key={key}
              className={`cursor-pointer py-2 px-3 mb-2 rounded text-sm ${
                activeSection === key ? 'bg-black text-white' : 'hover:bg-gray-200'
              }`}
              onClick={() => setActiveSection(key)}
            >
              {title}
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content */}
      <div className="w-3/4 p-8">
        <h2 className="text-2xl font-bold mb-4 flex items-center whitespace-nowrap">
          Help <MdKeyboardArrowRight size={36} className="mx-1 text-gray-500" />{' '}
          {sections[activeSection]}
        </h2>
        <div className="text-gray-700">{content[activeSection]}</div>
      </div>
    </div>
  );
};

export default Help;
